/**
 * The page 02 map, as a Mapbox Static Images URL.
 *
 * Why not the interactive map the rest of the app uses: the deck is `display: none` on
 * screen (it exists only for the print view), so a Mapbox GL canvas created on mount has
 * no size and never renders its tiles - it prints as a flat blue block. Even resized on
 * beforeprint, GL streams tiles asynchronously and the print job does not wait for them.
 *
 * A static image is a single request that the browser treats like any other <img>, so it
 * is loaded and painted before printing.
 *
 * **Attribution is switched off in the image.** Mapbox bakes its wordmark into the bottom
 * left of the returned picture and "(c) Mapbox (c) OpenStreetMap" into the bottom right; the
 * business asked for both off the map on 2026-09-12, and `logo` and `attribution` are the
 * documented parameters for that. Note what that does not do: Mapbox's terms and the
 * OpenStreetMap licence still require the credit to appear, and turning these off moves the
 * obligation onto the page rather than lifting it. Nothing on page 02 carries it today, so
 * the deck is out of compliance until a credit is placed somewhere on the sheet.
 */

const ACCESS_TOKEN = 'pk.eyJ1IjoiYW5kcmVlYW1pcmNlYSIsImEiOiJja2lpa25rNzQwYWV0MnNxcGdscDVnbGc3In0.YW3ma8Gr7xlYFsAfLwTL1A';
const STYLE = 'mapbox/streets-v11';

/**
 * Metro-wide, not neighbourhood-level.
 *
 * The template's own map shows the whole city the property sits in - its Oklahoma City
 * example spans Crescent in the north to Purcell in the south, about 0.93 degrees of
 * latitude. Across the 385px panel that works out at zoom 8 (which covers ~0.86 deg);
 * zoom 9 covers ~0.43 deg and zoom 12, which this used to be, is a few streets.
 */
const ZOOM = 8;

// Accent-green pin, matching the deck.
const PIN = 'pin-l+8dc264';

/**
 * @param {number} latitude
 * @param {number} longitude
 * @param {number} width  in CSS px; requested at @2x for print sharpness
 * @param {number} height in CSS px
 * @returns {string} URL, or '' when the property has no usable coordinates
 */
export function staticMapUrl(latitude, longitude, width, height) {
    const lat = parseFloat(latitude);
    const lng = parseFloat(longitude);
    if (!isFinite(lat) || !isFinite(lng) || (lat === 0 && lng === 0)) return '';

    const marker = `${PIN}(${lng},${lat})`;
    const centre = `${lng},${lat},${ZOOM}`;
    // Static Images caps a side at 1280px, so @2x is only safe up to 640.
    const scale = width <= 640 && height <= 640 ? '@2x' : '';

    return `https://api.mapbox.com/styles/v1/${STYLE}/static/${marker}/${centre}/`
        + `${Math.round(width)}x${Math.round(height)}${scale}`
        + `?access_token=${ACCESS_TOKEN}`
        // The wordmark bottom left and the "(c) Mapbox (c) OpenStreetMap" line bottom
        // right. See the note at the top of this file before removing these.
        + '&logo=false&attribution=false';
}

/**
 * Looks up coordinates for a free-text address.
 *
 * Needed because plenty of property records have no latitude/longitude - the previous
 * deck's live map silently centred those on 0,0, which is open ocean, and printed as a
 * flat blue rectangle. Falling back to the address means the page shows the right place
 * instead of the Atlantic.
 *
 * @returns {Promise<{lat: number, lng: number}|null>}
 */
export async function geocode(addressParts) {
    const query = (addressParts || []).filter(Boolean).join(', ').trim();
    if (!query) return null;

    const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json`
        + `?limit=1&access_token=${ACCESS_TOKEN}`;
    try {
        const res = await fetch(url);
        if (!res.ok) return null;
        const body = await res.json();
        const centre = body && body.features && body.features[0] && body.features[0].center;
        if (!centre || centre.length !== 2) return null;
        return {lng: centre[0], lat: centre[1]};
    } catch (e) {
        return null;
    }
}

/**
 * The property's own coordinates when it has them, otherwise geocoded from its address.
 * Resolves to '' when neither is available, so the panel prints empty rather than wrong.
 */
export async function resolveStaticMapUrl(mapProps, property, width, height) {
    const props = mapProps || {};
    const direct = staticMapUrl(props.latitude, props.longitude, width, height);
    if (direct) return direct;

    const p = property || {};
    const coords = await geocode([p.address, p.city, p.state, p.zip]);
    return coords ? staticMapUrl(coords.lat, coords.lng, width, height) : '';
}
